/**
 * Session detail tab (reports).
 */
import * as React from 'react';
import { BookOpen, FileEdit, Star } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DetailsCard } from '@/components/common/DetailsCard';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { getReports } from '@/services/inperson/reports.service';
import { useNavigateToReport } from '@/hooks/useNavigateToReport';
import { SESSION_IN_PERSON_REPORT_EMPTY_MESSAGE } from '@/components/sessions/session-configs';
import type { SessionWithParticipants } from '@/types/inperson/session.types';

/** Props for {@link SessionReportsTab}. */
interface SessionReportsTabProps {
  session: SessionWithParticipants;
}

/** Filled vs empty star class for the report rating. */
const starClassName = (filled: boolean): string => {
  if (filled) return 'w-5 h-5 fill-[var(--color-3-nuance-1)] text-[var(--color-3-nuance-1)]';
  return 'w-5 h-5 text-muted-foreground';
};

/** Loading / empty / rating+comment body for the reports tab. */
const ReportsTabBody: React.FC<{
  sessionType: string;
  isLoading: boolean;
  report: { rating?: number | null; comment?: string | null } | undefined;
  onOpenReport: () => void;
}> = ({ sessionType, isLoading, report, onOpenReport }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="md" label="Chargement du compte rendu..." />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-8">
        <EmptyPlaceholder
          message={
            SESSION_IN_PERSON_REPORT_EMPTY_MESSAGE[sessionType] ??
            'Aucun compte rendu disponible pour cette séance'
          }
        />
        <div className="mt-4">
          <Button variant="outline" onClick={onOpenReport} icon={FileEdit}>
            Accéder aux comptes rendus
          </Button>
        </div>
      </div>
    );
  }

  const rating = report.rating ?? 0;

  return (
    <div className="space-y-4">
      {report.rating != null && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Niveau d'engagement des participants
          </p>
          <div className="flex items-center gap-1">
            {Array.from({ length: 5 }, (_, i) => (
              <Star key={i} className={starClassName(i < rating)} />
            ))}
            <span className="ml-2 text-sm font-medium">{report.rating}/5</span>
          </div>
        </div>
      )}
      {Boolean(report.comment?.trim()) && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Synthèse de la réunion d'information
          </p>
          <blockquote className="border-l-4 border-[var(--color-1-nuance-1)] pl-3 text-sm text-foreground whitespace-pre-wrap italic">
            {report.comment}
          </blockquote>
        </div>
      )}
      <div className="pt-4 border-t border-border">
        <Button variant="outline" onClick={onOpenReport} icon={FileEdit}>
          Voir le compte rendu complet
        </Button>
      </div>
    </div>
  );
};

/** Session detail tab (reports). */
export const SessionReportsTab: React.FC<SessionReportsTabProps> = ({ session }) => {
  const { navigateToReport } = useNavigateToReport();
  const { data: reports, isLoading: isLoadingReports } = useQuery({
    queryKey: ['reports', 'session', session.id],
    queryFn: () => getReports(session.id),
  });

  return (
    <div className="details-view-grid">
      <DetailsCard
        icon={BookOpen}
        title="Compte rendu — Réunion d'information"
        colorName="color-1"
        colSpan="full"
      >
        <ReportsTabBody
          sessionType={session.type}
          isLoading={isLoadingReports}
          report={reports?.[0]}
          onOpenReport={() => navigateToReport(session.id)}
        />
      </DetailsCard>
    </div>
  );
};
